// ============================================================================
// TRAINING GUIDE ASSISTANT - RAG-Powered Q&A Chat Panel
// ============================================================================

import { useState, useRef, useEffect, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useAIStore } from '@/stores/aiStore'
import { knowledgeApi, getSessionToken, type PMTopic, type KnowledgeQueryResult } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Send,
  Loader2,
  X,
  Bot,
  User,
  BookOpen,
  AlertCircle,
  Sparkles,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================================================
// TYPES
// ============================================================================

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  sources?: KnowledgeQueryResult[]
  isStreaming?: boolean
}

interface TrainingGuideAssistantProps {
  onClose: () => void
  currentTopic?: PMTopic
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Use relative URLs to go through Vite's proxy (same as main client)
const API_URL = import.meta.env.VITE_API_URL ?? ''

// ============================================================================
// COMPONENT
// ============================================================================

export function TrainingGuideAssistant({ onClose, currentTopic }: TrainingGuideAssistantProps) {
  const { accountId } = useSessionStore()
  const { apiKey } = useAIStore()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Send message with RAG
  const sendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isStreaming || !accountId) return

    const userContent = inputValue.trim()
    setInputValue('')
    setError(null)
    setIsLoading(true)

    // Add user message
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: userContent,
      timestamp: new Date(),
    }
    setMessages(prev => [...prev, userMessage])

    // Add placeholder assistant message
    const assistantId = crypto.randomUUID()
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }
    setMessages(prev => [...prev, assistantMessage])
    setIsStreaming(true)

    try {
      // 1. Query knowledge base for relevant chunks
      const queryResponse = await knowledgeApi.query(accountId, {
        q: userContent,
        topK: 5,
        minSimilarity: 0.6,
      })

      const sources = queryResponse.results

      // 2. If no API key, show sources only without AI response
      if (!apiKey) {
        const noApiResponse = sources.length > 0
          ? `I found ${sources.length} relevant knowledge chunk${sources.length !== 1 ? 's' : ''} that may help answer your question. Please configure your Claude API key in Settings to get AI-powered answers.`
          : 'No relevant knowledge found. Try rephrasing your question or add more content to the knowledge base.'

        setMessages(prev =>
          prev.map(m =>
            m.id === assistantId
              ? { ...m, content: noApiResponse, sources, isStreaming: false }
              : m
          )
        )
        return
      }

      // 3. Stream response from knowledge-chat endpoint
      const token = getSessionToken()
      const response = await fetch(`${API_URL}/api/accounts/${accountId}/knowledge/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Claude-API-Key': apiKey,
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        credentials: 'include',
        body: JSON.stringify({
          question: userContent,
          topK: 5,
          minSimilarity: 0.6,
          topicFilter: currentTopic?.id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Chat failed: ${response.status}`)
      }

      // Handle streaming response
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      const decoder = new TextDecoder()
      let streamedContent = ''
      let parsedSources: KnowledgeQueryResult[] = []

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') continue

            try {
              const event = JSON.parse(data)

              if (event.type === 'text' && event.content) {
                streamedContent += event.content
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantId
                      ? { ...m, content: streamedContent }
                      : m
                  )
                )
              } else if (event.type === 'sources' && event.sources) {
                parsedSources = event.sources
              } else if (event.type === 'error') {
                throw new Error(event.message || 'Stream error')
              }
            } catch (parseErr) {
              // Skip non-JSON lines
            }
          }
        }
      }

      // Finalize message
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: streamedContent || 'No response generated.', sources: parsedSources, isStreaming: false }
            : m
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get response'
      setError(errorMessage)

      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: `Sorry, something went wrong: ${errorMessage}`, isStreaming: false }
            : m
        )
      )
    } finally {
      setIsLoading(false)
      setIsStreaming(false)
    }
  }, [inputValue, isLoading, isStreaming, accountId, apiKey, currentTopic])

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="fixed top-20 right-6 bottom-24 w-96 bg-white rounded-xl shadow-2xl border border-warm-200 flex flex-col z-30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-warm-200 bg-gradient-to-r from-market/5 to-warm-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-market flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-text text-sm">Training Assistant</h3>
            <p className="text-xs text-text-muted">Ask about PM knowledge</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <WelcomeMessage currentTopic={currentTopic} />
        )}

        {messages.map(message => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {error && (
          <div className="flex items-center gap-2 text-error text-sm bg-red-50 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-warm-200 bg-warm-50">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={currentTopic ? `Ask about ${currentTopic.name}...` : 'Ask a question...'}
            className="flex-1 resize-none rounded-lg border border-warm-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-market/20 focus:border-market"
            rows={2}
            disabled={isLoading || isStreaming}
          />
          <Button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading || isStreaming}
            className="bg-market hover:bg-market/90 text-white h-auto"
          >
            {isLoading || isStreaming ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        {!apiKey && (
          <p className="text-xs text-text-muted mt-2">
            Add your Claude API key in Settings for AI-powered answers.
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function WelcomeMessage({ currentTopic }: { currentTopic?: PMTopic }) {
  return (
    <div className="text-center py-6">
      <div className="w-12 h-12 rounded-xl bg-market/10 flex items-center justify-center mx-auto mb-3">
        <Sparkles className="w-6 h-6 text-market" />
      </div>
      <h4 className="font-semibold text-text mb-1">PM Knowledge Assistant</h4>
      <p className="text-sm text-text-secondary">
        {currentTopic
          ? `Ask questions about ${currentTopic.name} or any PM topic.`
          : 'Ask questions about product management best practices.'}
      </p>
      <div className="mt-4 space-y-2">
        <p className="text-xs text-text-muted">Try asking:</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {['What is RICE prioritization?', 'How do I run user interviews?', 'Explain OKRs'].map(q => (
            <Badge
              key={q}
              variant="outline"
              className="text-xs cursor-pointer hover:bg-market/10 hover:text-market hover:border-market/20"
            >
              {q}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  )
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-business' : 'bg-market'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      <div className={cn('flex-1 min-w-0', isUser && 'text-right')}>
        <div
          className={cn(
            'inline-block rounded-lg px-3 py-2 text-sm max-w-full',
            isUser
              ? 'bg-business/10 text-text'
              : 'bg-warm-100 text-text'
          )}
        >
          <p className="whitespace-pre-wrap break-words text-left">{message.content}</p>
          {message.isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-market/50 animate-pulse ml-1"></span>
          )}
        </div>

        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-2 text-left">
            <p className="text-xs text-text-muted mb-1 flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              Sources
            </p>
            <div className="space-y-1">
              {message.sources.slice(0, 3).map((source, idx) => (
                <div
                  key={source.chunkId}
                  className="bg-warm-50 rounded px-2 py-1 text-xs flex items-center justify-between gap-2"
                >
                  <span className="truncate text-text-secondary">{source.sourceTitle}</span>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {Math.round(source.similarity * 100)}%
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TrainingGuideAssistant
